import React, { useEffect, useState } from 'react';
import { getTopMatchKombis } from '../../../api/api';
import { TopMatchKombiItem } from '../../../api/types';
import { useTranslation } from '../../../i18n';

function formatOdds(val: string | number | undefined): string {
  if (val === undefined || val === null || val === '') return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

interface DesktopTopMatchKombisPageProps {
  onAddMatchKombi?: (item: TopMatchKombiItem) => void;
  onOpenBuilder?: (item: TopMatchKombiItem) => void;
  isSelected?: (item: TopMatchKombiItem) => boolean;
}

export const DesktopTopMatchKombisPage: React.FC<DesktopTopMatchKombisPageProps> = ({
  onAddMatchKombi,
  onOpenBuilder,
  isSelected,
}) => {
  const { locale, t } = useTranslation();
  const [items, setItems] = useState<TopMatchKombiItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    // Fetch a larger list for the dedicated page
    getTopMatchKombis({ view: 'home', limit: 20, locale })
      .then((res) => {
        if (!active) return;
        setItems(res.items || []);
      })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [locale]);

  if (!loaded) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-tipico-red border-t-transparent" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm p-10 text-center text-gray-400 text-[15px] font-medium">
        Keine MatchKombis verfügbar.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 w-full pb-10">
      {/* Title */}
      <div className="px-2 mb-2">
        <h2 className="text-[20px] font-black text-gray-900">MatchKombis</h2>
      </div>

      <div className="flex flex-col gap-4">
        {items.map((item) => {
          const selected = isSelected?.(item) || false;
          return (
            <div
              key={item.combination_id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col hover:shadow-md transition"
            >
              {/* Match header */}
              <div className="relative -mx-[1px] -mt-[1px] h-[44px] flex items-center px-4 rounded-t-lg overflow-hidden border-b border-gray-100">
                <div className="absolute top-0 left-0 h-full w-[80%] rounded-tl-lg" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
                <div className="absolute top-0 left-[calc(80%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }} />
                <span className="text-[14px] font-bold text-white relative z-10 leading-tight truncate">
                  {item.event_name}
                </span>
              </div>

              {/* Selections */}
              <div className="flex-1 flex flex-col gap-2 px-4 py-4">
                {item.selections.map((sel, i) => (
                  <div key={`${sel.market_id}-${sel.specifiers}-${sel.outcome_id}-${i}`} className="flex items-start gap-2">
                    <span className="mt-[4px] w-[8px] h-[8px] rounded-full bg-[#c8102e] flex-shrink-0" />
                    <span className="text-[14px] leading-snug text-gray-700 truncate">
                      <span className="text-gray-500">{sel.market_name}: </span>
                      <span className="font-bold text-gray-900">{sel.outcome_name}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer: open Bet Builder+ (left) · add-to-slip odds box (right) */}
              <div className="flex items-stretch gap-2 px-3 pb-3">
                <button
                  onClick={() => onOpenBuilder?.(item)}
                  disabled={!onOpenBuilder}
                  className="flex-1 border border-gray-300 rounded-md py-2.5 text-[14px] font-bold text-gray-800 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('section.createMkPlus')}
                </button>
                <button
                  onClick={() => onAddMatchKombi?.(item)}
                  disabled={!onAddMatchKombi}
                  className={`min-w-[80px] rounded-md flex items-center justify-center px-3 text-[15px] font-black border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    selected
                      ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]'
                      : 'bg-[#f0f2f5] border-[#c0c5cc] text-gray-900 hover:bg-[#e3e6eb] dark:bg-[#252525] dark:border-[#333] dark:text-white dark:hover:bg-[#333]'
                  }`}
                >
                  {formatOdds(item.combined_odds)}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
